import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getPluginConfig } from '../config';
import useAddressSearch from '../hooks/useAddressSearch';
import {
  DEFAULT_HINT,
  DEFAULT_LABEL,
  MIN_SEARCH_LENGTH,
  NO_RESULTS_MESSAGE,
  SEARCH_STATUS,
  UNAVAILABLE_MESSAGE
} from '../constants';

const CLASSNAME = 'address-lookup';

const getStatusMessage = ({ status, items, text, isExpanded }) => {
  if (status === SEARCH_STATUS.ERROR) return { message: UNAVAILABLE_MESSAGE, isVisible: true };
  if (status !== SEARCH_STATUS.DONE || text.trim().length < MIN_SEARCH_LENGTH) return null;
  if (!items.length) return { message: NO_RESULTS_MESSAGE, isVisible: true };
  if (!isExpanded) return null;

  const count = items.length === 1 ? '1 result' : `${items.length} results`;
  return { message: `${count} found, use the up and down arrow keys to choose`, isVisible: false };
};

/**
 * Address search following the WAI-ARIA combobox pattern. Picking an address calls
 * `onSelect` with the normalised address (see normalise-address.js); picking a group,
 * such as a street or a block of flats, lists the addresses inside it instead.
 *
 * Renders nothing while the plugin has no key. Searches are limited to `country`, and
 * changing it clears the search. Load it with next/dynamic and `ssr: false`.
 */
const AddressLookup = ({ id, country, onSelect, label, hint, className }) => {
  const [text, setText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { status, items, isStale, search, open, choose, reset } = useAddressSearch(country);
  // Picking an address waits for Retrieve, so the form's latest handler is called rather than
  // the one from the render the pick started in, which may hold stale form values
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  // Set while a picked result is being fetched, so a double click or a repeated Enter does
  // not start a second, charged, Retrieve
  const isSelectingRef = useRef(false);

  useEffect(() => {
    setText('');
    setIsOpen(false);
    reset();
  }, [country, reset]);

  useEffect(() => setActiveIndex(-1), [items]);

  // The list scrolls, so keep the option the arrow keys move to in sight
  useEffect(() => {
    if (activeIndex < 0 || typeof document === 'undefined') return;
    const option = document.getElementById(`${id}-option-${activeIndex}`);
    if (option && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, id]);

  if (!getPluginConfig().enabled) return null;

  const listId = `${id}-listbox`;
  const hintId = `${id}-hint`;
  const optionId = index => `${id}-option-${index}`;
  const isExpanded = isOpen && status !== SEARCH_STATUS.ERROR && items.length > 0;
  const statusMessage = getStatusMessage({ status, items, text, isExpanded });

  const handleChange = ({ target: { value } }) => {
    setText(value);
    setIsOpen(true);
    search(value);
  };

  const handleSelect = async item => {
    // results for text the customer has since changed are on screen only until the new ones land
    if (isSelectingRef.current || isStale) return;
    isSelectingRef.current = true;

    try {
      if (item.isContainer) {
        setIsOpen(true);
        await open(item);
        return;
      }

      const address = await choose(item);
      if (!address) return;
      setText('');
      setIsOpen(false);
      onSelectRef.current(address);
    } finally {
      isSelectingRef.current = false;
    }
  };

  const moveActive = step => {
    if (!items.length) return;
    if (!isExpanded) {
      setIsOpen(true);
      return;
    }
    // With no option active yet, ArrowDown starts at the first result and ArrowUp at the last
    setActiveIndex(current => {
      if (current < 0) return step > 0 ? 0 : items.length - 1;
      return (current + step + items.length) % items.length;
    });
  };

  const handleKeyDown = event => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Enter':
        if (isExpanded && activeIndex >= 0) {
          event.preventDefault();
          if (!isStale) handleSelect(items[activeIndex]);
        }
        break;
      case 'Escape':
        if (isExpanded) {
          event.preventDefault();
          setIsOpen(false);
        } else if (text) {
          event.preventDefault();
          setText('');
          reset();
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={`${CLASSNAME}${className ? ` ${className}` : ''}`}>
      <label className={`${CLASSNAME}__label`} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`${CLASSNAME}__input`}
        type="text"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={isExpanded}
        aria-controls={listId}
        aria-describedby={hintId}
        aria-activedescendant={isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setIsOpen(false)}
      />
      <span id={hintId} className={`${CLASSNAME}__hint`}>
        {hint}
      </span>
      <ul
        id={listId}
        className={`${CLASSNAME}__listbox`}
        role="listbox"
        aria-label={`${label} results`}
        aria-busy={isStale}
        hidden={!isExpanded}>
        {isExpanded &&
          items.map((item, index) => (
            <li
              key={item.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={[
                `${CLASSNAME}__option`,
                index === activeIndex && `${CLASSNAME}__option--active`,
                item.isContainer && `${CLASSNAME}__option--group`
              ]
                .filter(Boolean)
                .join(' ')}
              // keeps focus in the input, so the list does not close before the click
              onMouseDown={event => event.preventDefault()}
              onClick={() => handleSelect(item)}>
              <span className={`${CLASSNAME}__option-text`}>{item.text}</span>
              {item.description && (
                <span className={`${CLASSNAME}__option-description`}>{item.description}</span>
              )}
            </li>
          ))}
      </ul>
      <div
        role="status"
        className={`${CLASSNAME}__status${
          statusMessage && statusMessage.isVisible ? '' : ` ${CLASSNAME}__status--hidden`
        }`}>
        {statusMessage ? statusMessage.message : ''}
      </div>
    </div>
  );
};

AddressLookup.propTypes = {
  id: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  country: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  className: PropTypes.string
};

AddressLookup.defaultProps = {
  country: '',
  label: DEFAULT_LABEL,
  hint: DEFAULT_HINT,
  className: ''
};

export default AddressLookup;
